import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { EnvironmentChip } from "@/components/EnvironmentBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Building2, ShieldCheck, KeyRound, Palette, TestTube2, Rocket, LayoutDashboard, Check,
} from "lucide-react";

/**
 * Partner Self-Service Onboarding Portal — RC2 P1.
 *
 * 8-stage wizard. Existing applications are recovered from
 * `GET /partner-onboarding/mine`; new applications are created by
 * `POST /partner-onboarding/applications`. Each stage advances the
 * server-side state machine via a dedicated endpoint.
 */
const STAGES = [
  { key: "registration",           title: "Organisation Registration", Icon: Building2 },
  { key: "identity_verification",  title: "Identity Verification",     Icon: ShieldCheck },
  { key: "compliance_review",      title: "Approval Review",           Icon: Check },
  { key: "brand_configuration",    title: "Brand Configuration",       Icon: Palette },
  { key: "integration_testing",    title: "Integration Testing",       Icon: TestTube2 },
  { key: "production",             title: "Production Activation",     Icon: Rocket },
  { key: "dashboard",              title: "Partner Dashboard",         Icon: LayoutDashboard },
];

export default function PartnerOnboarding() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [industries, setIndustries] = useState([]);
  const [apps, setApps] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/partner-onboarding/industries");
        setIndustries(r.data.industries || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const loadMine = async () => {
    if (!user) return;
    try {
      const r = await api.get("/partner-onboarding/mine");
      setApps(r.data.items || []);
      if (r.data.items?.length && !active) setActive(r.data.items[0]);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadMine(); /* eslint-disable-next-line */ }, [user?.user_id]);

  const currentStage = useMemo(() => active?.stage || "registration", [active]);
  const stageIndex = STAGES.findIndex(s => s.key === currentStage);

  return (
    <div>
      <Navbar />
      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="kicker flex items-center gap-2">Partner Onboarding <EnvironmentChip env={active?.environment || "sandbox"} testid="onboarding-env-chip" /></div>
            <h1 className="display text-4xl mt-1">Self-service partner onboarding</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Register your organisation, submit identity evidence, receive sandbox credentials, brand your workspace, complete integration testing and go live — all without contacting THOM staff.</p>
          </div>
          {apps.length > 0 && (
            <Select value={active?.id || ""} onValueChange={(v) => setActive(apps.find(a => a.id === v))}>
              <SelectTrigger className="w-[260px]" data-testid="onboarding-app-select"><SelectValue placeholder="Select application" /></SelectTrigger>
              <SelectContent>
                {apps.map(a => <SelectItem key={a.id} value={a.id}>{a.ref} · {a.organisation?.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Stage stepper */}
        <div className="grid md:grid-cols-7 gap-2 mb-8" data-testid="onboarding-stepper">
          {STAGES.map((s, i) => {
            const done = active && i < stageIndex;
            const cur = active && i === stageIndex;
            return (
              <div key={s.key} className={`p-3 border rounded-sm ${cur ? "border-primary bg-primary/5" : done ? "border-emerald-500/40 bg-emerald-500/5" : "border-border bg-card"}`}>
                <div className="flex items-center gap-2">
                  <s.Icon className={`h-4 w-4 ${done ? "text-emerald-700" : cur ? "text-primary" : "text-muted-foreground"}`} />
                  <span className="mono text-[10px] uppercase tracking-widest">Stage {i + 1}</span>
                </div>
                <div className="text-[12.5px] mt-1 font-medium">{s.title}</div>
              </div>
            );
          })}
        </div>

        {/* Body */}
        {!active && <StageRegistration industries={industries} onCreated={(a) => { setApps([a, ...apps]); setActive(a); toast.success("Application submitted"); }} />}
        {active && active.state === "submitted"       && <StageIdentity app={active} onDone={loadMine} />}
        {active && active.state === "identity_verified" && <StageWaitReview />}
        {active && active.state === "under_review"    && <StageWaitReview />}
        {active && active.state === "approved"        && <StageAwaitSandbox app={active} />}
        {active && active.state === "sandbox_provisioned" && <StageBranding app={active} onDone={loadMine} />}
        {active && active.state === "integration_testing" && <StageIntegration app={active} onDone={loadMine} />}
        {active && active.state === "production_requested" && <StageWaitProduction />}
        {active && active.state === "live"            && <StageLive app={active} onOpenDashboard={() => nav(`/orgs/${active.sandbox?.organisation_id || ""}`)} />}
        {active && ["rejected", "suspended", "archived"].includes(active.state) && <StageBlocked app={active} />}
      </div>
      <Footer />
    </div>
  );
}

// ─── Stage 1 · Registration ────────────────────────────────────────────────
function StageRegistration({ industries, onCreated }) {
  const [form, setForm] = useState({
    organisation_name: "", registration_number: "", country: "ZW", industry: "bank",
    tax_number: "", physical_address: "", website: "",
    contact_name: "", contact_role: "Administrator", contact_email: "", contact_mobile: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/partner-onboarding/applications", {
        organisation_name: form.organisation_name,
        registration_number: form.registration_number,
        country: form.country,
        industry: form.industry,
        tax_number: form.tax_number || null,
        physical_address: form.physical_address,
        website: form.website || null,
        contact: { name: form.contact_name, role: form.contact_role, email: form.contact_email, mobile: form.contact_mobile },
      });
      onCreated(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };
  const f = (k) => (v) => setForm({ ...form, [k]: typeof v === "string" ? v : v.target.value });
  return (
    <form onSubmit={submit} className="border border-border bg-card p-6 grid md:grid-cols-2 gap-4" data-testid="stage-registration">
      <div className="md:col-span-2 kicker">Stage 1 · Organisation Registration</div>
      <Field label="Organisation Name"><Input value={form.organisation_name} onChange={f("organisation_name")} required data-testid="onb-org-name" /></Field>
      <Field label="Registration Number"><Input value={form.registration_number} onChange={f("registration_number")} required data-testid="onb-reg-no" /></Field>
      <Field label="Country (ISO-2)"><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} maxLength={2} required /></Field>
      <Field label="Industry">
        <Select value={form.industry} onValueChange={f("industry")}>
          <SelectTrigger data-testid="onb-industry"><SelectValue /></SelectTrigger>
          <SelectContent>{industries.map(i => <SelectItem key={i} value={i}>{i.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Tax Number"><Input value={form.tax_number} onChange={f("tax_number")} /></Field>
      <Field label="Website"><Input value={form.website} onChange={f("website")} placeholder="https://…" /></Field>
      <Field label="Physical Address" full><Textarea value={form.physical_address} onChange={f("physical_address")} rows={2} required /></Field>
      <div className="md:col-span-2 mt-2 kicker">Administrator Contact</div>
      <Field label="Full name"><Input value={form.contact_name} onChange={f("contact_name")} required data-testid="onb-contact-name" /></Field>
      <Field label="Role"><Input value={form.contact_role} onChange={f("contact_role")} /></Field>
      <Field label="Email"><Input type="email" value={form.contact_email} onChange={f("contact_email")} required data-testid="onb-contact-email" /></Field>
      <Field label="Mobile"><Input value={form.contact_mobile} onChange={f("contact_mobile")} required data-testid="onb-contact-mobile" /></Field>
      <div className="md:col-span-2 flex justify-end">
        <Button type="submit" disabled={busy} data-testid="onb-submit-registration">{busy ? "Submitting…" : "Submit Application"}</Button>
      </div>
    </form>
  );
}

// ─── Stage 2 · Identity Verification ───────────────────────────────────────
function StageIdentity({ app, onDone }) {
  const [kind, setKind] = useState("company_registration");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const attach = async () => {
    setBusy(true);
    try {
      await api.post(`/partner-onboarding/applications/${app.id}/identity`, { kind, reference, note });
      toast.success("Evidence attached");
      setReference(""); setNote("");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border bg-card p-6 space-y-4" data-testid="stage-identity">
      <div className="kicker">Stage 2 · Identity Verification</div>
      <p className="text-sm text-muted-foreground">Attach evidence for company registration, tax certificate, administrator identity and contact verification. Attach as many items as required.</p>
      <div className="grid md:grid-cols-3 gap-3">
        <Field label="Evidence type">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="company_registration">Company Registration</SelectItem>
              <SelectItem value="tax_certificate">Tax Certificate</SelectItem>
              <SelectItem value="admin_id">Administrator ID</SelectItem>
              <SelectItem value="admin_email_otp">Admin Email OTP</SelectItem>
              <SelectItem value="admin_mobile_otp">Admin Mobile OTP</SelectItem>
              <SelectItem value="letter_of_authorisation">Letter of Authorisation</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Reference (file id / OTP token)"><Input value={reference} onChange={(e) => setReference(e.target.value)} required data-testid="onb-evidence-ref" /></Field>
        <Field label="Note"><Input value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </div>
      <div className="flex justify-between items-center">
        <div className="text-[12px] text-muted-foreground">{app.identity_evidence?.length || 0} evidence item(s) attached</div>
        <Button onClick={attach} disabled={busy || !reference} data-testid="onb-attach-evidence">{busy ? "Attaching…" : "Attach evidence"}</Button>
      </div>
      {(app.identity_evidence || []).length > 0 && (
        <div className="mt-3 border-t border-border pt-3 space-y-1 text-[12.5px]">
          {(app.identity_evidence || []).map((e, i) => (
            <div key={i} className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-600" /><span className="mono uppercase text-[10px] tracking-widest">{e.kind}</span> — {e.reference}</div>
          ))}
        </div>
      )}
    </div>
  );
}

const StageWaitReview = () => (
  <div className="border border-amber-500/40 bg-amber-50 p-6" data-testid="stage-wait-review">
    <div className="mono text-[10px] uppercase tracking-widest text-amber-800">Awaiting Review</div>
    <div className="text-sm mt-1 text-amber-900">A THOM Verify reviewer will validate the submitted evidence and either approve, reject or suspend the application. You will be notified via email and in-app.</div>
  </div>
);

const StageAwaitSandbox = ({ app }) => (
  <div className="border border-emerald-500/40 bg-emerald-50 p-6" data-testid="stage-await-sandbox">
    <div className="mono text-[10px] uppercase tracking-widest text-emerald-800">Approved</div>
    <div className="text-sm mt-1 text-emerald-900">Application <span className="mono">{app.ref}</span> approved. A THOM administrator will provision your sandbox environment — sandbox API key, OAuth client and webhook secret will be delivered to <span className="mono">{app.contact?.email}</span> via Resend.</div>
  </div>
);

function StageBranding({ app, onDone }) {
  const s = app.sandbox || {};
  const [brand, setBrand] = useState({
    logo_url: app.brand?.logo_url || "",
    primary: app.brand?.primary || "#0A0A0A",
    secondary: app.brand?.secondary || "#B08C4A",
    contact_email: app.brand?.contact_email || app.contact?.email || "",
    contact_phone: app.brand?.contact_phone || "",
    tagline: app.brand?.tagline || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/partner-onboarding/applications/${app.id}/branding`, brand);
      toast.success("Branding saved — proceed to integration testing");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="stage-branding">
      <div className="border-2 border-emerald-500 bg-emerald-50 p-4">
        <div className="mono text-[10px] uppercase tracking-widest text-emerald-800">Sandbox Provisioned</div>
        <div className="text-[13px] mt-1 text-emerald-900">Store the credentials below securely — the secrets are shown once only.</div>
        <div className="grid md:grid-cols-2 gap-2 mt-3 text-[12px]">
          <KV k="Organisation" v={s.organisation_id} />
          <KV k="Environment" v="sandbox" />
          <KV k="API Key ID" v={s.api_key_id} mono />
          <KV k="OAuth Client" v={s.oauth_client_id} mono />
          <KV k="OAuth Secret" v={s.oauth_client_secret_once} mono />
          <KV k="Webhook Secret" v={s.webhook_secret_once} mono />
        </div>
      </div>
      <div className="border border-border bg-card p-6 grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2 kicker">Stage 5 · Brand Configuration</div>
        <Field label="Logo URL"><Input value={brand.logo_url} onChange={(e) => setBrand({ ...brand, logo_url: e.target.value })} /></Field>
        <Field label="Primary Colour"><Input value={brand.primary} onChange={(e) => setBrand({ ...brand, primary: e.target.value })} /></Field>
        <Field label="Secondary Colour"><Input value={brand.secondary} onChange={(e) => setBrand({ ...brand, secondary: e.target.value })} /></Field>
        <Field label="Tagline"><Input value={brand.tagline} onChange={(e) => setBrand({ ...brand, tagline: e.target.value })} /></Field>
        <Field label="Contact Email"><Input type="email" value={brand.contact_email} onChange={(e) => setBrand({ ...brand, contact_email: e.target.value })} /></Field>
        <Field label="Contact Phone"><Input value={brand.contact_phone} onChange={(e) => setBrand({ ...brand, contact_phone: e.target.value })} /></Field>
        <div className="md:col-span-2 flex justify-end">
          <Button onClick={save} disabled={busy} data-testid="onb-save-brand">{busy ? "Saving…" : "Save & continue"}</Button>
        </div>
      </div>
    </div>
  );
}

function StageIntegration({ app, onDone }) {
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const request = async () => {
    setBusy(true);
    try {
      await api.post(`/partner-onboarding/applications/${app.id}/production-request`, {
        compliance_checklist_ack: true, notes,
      });
      toast.success("Production request submitted");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="stage-integration">
      <div className="border border-border bg-card p-6">
        <div className="kicker">Stage 6 · Integration Testing</div>
        <p className="text-sm text-muted-foreground mt-1">Exercise the sandbox via the Developer Portal — send test verification lookups, wire your webhook receiver, verify signatures and inspect the request history.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <a href="/admin/api" target="_blank" rel="noreferrer"><Button variant="outline" size="sm">Developer Portal</Button></a>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/docs`} target="_blank" rel="noreferrer"><Button variant="outline" size="sm">OpenAPI Reference</Button></a>
          <a href="/verify/certificate" target="_blank" rel="noreferrer"><Button variant="outline" size="sm">Public Verification Portal</Button></a>
        </div>
      </div>
      <div className="border border-border bg-card p-6">
        <div className="kicker">Stage 7 · Request Production Activation</div>
        <p className="text-sm text-muted-foreground mt-1">When your integration is complete, submit a Production Activation Request. A THOM compliance officer will review your logs, credentials, webhook signature verification and rate-limit posture before issuing production credentials.</p>
        <Textarea placeholder="Notes for the compliance reviewer — expected volumes, go-live date, integration owner…" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-3" data-testid="onb-production-notes" />
        <div className="mt-3 flex justify-end">
          <Button onClick={request} disabled={busy} data-testid="onb-request-production">{busy ? "Submitting…" : "Request Production Activation"}</Button>
        </div>
      </div>
    </div>
  );
}

const StageWaitProduction = () => (
  <div className="border border-violet-500/40 bg-violet-50 p-6" data-testid="stage-wait-production">
    <div className="mono text-[10px] uppercase tracking-widest text-violet-800">Compliance Review</div>
    <div className="text-sm mt-1 text-violet-900">Your Production Activation Request is under review. A THOM Compliance Officer will validate your sandbox history, webhook signatures and rate-limit posture. You'll be notified when production credentials are issued.</div>
  </div>
);

const StageLive = ({ app, onOpenDashboard }) => (
  <div className="space-y-4" data-testid="stage-live">
    <div className="border-2 border-emerald-500 bg-emerald-50 p-5">
      <div className="mono text-[10px] uppercase tracking-widest text-emerald-800">Live · Production</div>
      <div className="text-[15px] mt-1 text-emerald-900 font-semibold">Congratulations — {app.organisation?.name} is live on THOM Verify.</div>
      <div className="mt-3 grid md:grid-cols-2 gap-2 text-[12px]">
        <KV k="Production API Key" v={app.production?.credentials?.api_key_id} mono />
        <KV k="Production OAuth Client" v={app.production?.credentials?.oauth_client_id} mono />
      </div>
    </div>
    <Button onClick={onOpenDashboard} data-testid="onb-open-workspace">Open Partner Workspace →</Button>
  </div>
);

const StageBlocked = ({ app }) => (
  <div className="border-2 border-red-500 bg-red-50 p-6" data-testid="stage-blocked">
    <div className="mono text-[10px] uppercase tracking-widest text-red-800">Application {app.state}</div>
    <div className="text-sm mt-1 text-red-900">{app.review?.note || "Please contact a THOM administrator to proceed."}</div>
  </div>
);

const Field = ({ label, full, children }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
    {children}
  </label>
);
const KV = ({ k, v, mono }) => (
  <div className="grid grid-cols-[130px_1fr] gap-2 py-0.5">
    <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{k}</span>
    <span className={mono ? "mono text-[11px] break-all" : ""}>{v || "—"}</span>
  </div>
);
